import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { GetCompliancesList, AddCompliance, ListCompliance } from '../store/slices/reports';
import Loader from '../common/Loader';
import AdminLayout from './AdminLayout';
import { toast } from 'react-hot-toast';
import { API_BASE_URL } from '../utils/constants/api.constants';

const Compliance = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [openFormId, setOpenFormId] = useState(null);
  const [compliancesList, setCompliancesList] = useState([]);
  const [listCompliance, setListCompliance] = useState([]);

  const user = JSON.parse(localStorage.getItem('user'));
  const id = user?.data?.profile_id;

  // Form states per compliance ID to prevent shared field values
  const [formDataMap, setFormDataMap] = useState({});
  const [errorsMap, setErrorsMap] = useState({});

  const getFormData = (complianceId) => {
    return formDataMap[complianceId] || {
      date: '',
      document: null,
      remarks: '',
      next_due_date: '',
    };
  };

  const getErrors = (complianceId) => {
    return errorsMap[complianceId] || {
      date: '',
      document: '',
      remarks: '',
      next_due_date: '',
    };
  };

  const fetchComplianceData = useCallback((complianceid) => {
    dispatch(ListCompliance({ profile_id: id, compliance_id: complianceid }))
      .unwrap()
      .then((data) => {
        setListCompliance((prevList) => {
          const filtered = prevList.filter((item) => item.complianceId !== complianceid);
          return [...filtered, { complianceId: complianceid, data: data?.data || [] }];
        });
      })
      .catch(({ message }) => {
        console.log(message);
      });
  }, [dispatch, id]);

  useEffect(() => {
    setIsLoading(true);
    dispatch(GetCompliancesList({ profile_id: id }))
      .unwrap()
      .then((data) => {
        setIsLoading(false);
        const list = data?.data || [];
        setCompliancesList(list);
        list.forEach((compliance) => {
          fetchComplianceData(compliance.id);
        });
      })
      .catch(({ message }) => {
        setIsLoading(false);
        console.log(message);
      });
  }, [dispatch, fetchComplianceData, id]);

  const handleToggleForm = (complianceId) => {
    setOpenFormId((prevId) => (prevId === complianceId ? null : complianceId));
  };

  const handleInputChange = (complianceId, e) => {
    const { name, value, files } = e.target;

    setFormDataMap((prev) => ({
      ...prev,
      [complianceId]: {
        ...prev[complianceId],
        [name]: files ? files[0] : value,
      },
    }));

    setErrorsMap((prev) => ({
      ...prev,
      [complianceId]: {
        ...prev[complianceId],
        [name]: '',
      },
    }));
  };

  const validateForm = (complianceId, isRecurring) => {
    let isValid = true;
    const currentForm = getFormData(complianceId);
    const newErrors = {};

    if (!currentForm.date) {
      newErrors.date = 'Date is required';
      isValid = false;
    }

    if (!currentForm.document) {
      newErrors.document = 'Document upload is required';
      isValid = false;
    }

    if (isRecurring === '1' || isRecurring === true) {
      if (!currentForm.remarks) {
        newErrors.remarks = 'Remarks are required';
        isValid = false;
      }
      if (!currentForm.next_due_date) {
        newErrors.next_due_date = 'Next due date is required';
        isValid = false;
      }
    }

    setErrorsMap((prev) => ({
      ...prev,
      [complianceId]: newErrors,
    }));

    return isValid;
  };

  const handleSubmit = (e, complianceId, complianceName, is_completed) => {
    e.preventDefault();

    if (!validateForm(complianceId, is_completed)) {
      toast.error('Please fill in all required fields');
      return;
    }

    const currentForm = getFormData(complianceId);

    const formDataObj = new FormData();
    formDataObj.append('profile_id', id);
    formDataObj.append('compliance_id', complianceId);
    formDataObj.append('compliance_date', currentForm.date || '');
    formDataObj.append('compliance_document', currentForm.document || '');
    formDataObj.append('remarks', currentForm.remarks || '');
    formDataObj.append('next_due_date', currentForm.next_due_date || '');
    formDataObj.append('is_completed', is_completed);
    formDataObj.append('compliance_name', complianceName);

    setIsLoading(true);
    dispatch(AddCompliance(formDataObj))
      .unwrap()
      .then((data) => {
        setIsLoading(false);
        toast.success(data?.message || `${complianceName} entry saved successfully!`);

        // Clear form data for this specific compliance ID
        setFormDataMap((prev) => ({
          ...prev,
          [complianceId]: {
            date: '',
            document: null,
            remarks: '',
            next_due_date: '',
          },
        }));

        setOpenFormId(null);
        // Refresh compliance table data immediately for this compliance ID
        fetchComplianceData(complianceId);
      })
      .catch(({ message }) => {
        setIsLoading(false);
        toast.error(message || 'Failed to submit compliance');
        console.log(message);
      });
  };

  const getDocumentUrl = (doc) => {
    if (!doc) return '';
    if (doc.startsWith('http://') || doc.startsWith('https://')) {
      return doc;
    }
    const cleanDoc = doc.replace(/^\//, '');
    if (cleanDoc.startsWith('public/')) {
      return `${API_BASE_URL}/${cleanDoc}`;
    }
    if (cleanDoc.startsWith('uploads/')) {
      return `${API_BASE_URL}/public/${cleanDoc}`;
    }
    return `${API_BASE_URL}/public/uploads/compliances/${cleanDoc}`;
  };

  return (
    <AdminLayout>
      {isLoading && <Loader />}
      <div className="row content-body">
        <div className="container">
          <div className="page-header">
            <div>
              <h2 className="main-content-title tx-24 mg-b-5">One Time Compliance</h2>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <form onSubmit={(e) => e.preventDefault()}>
                <div className="card custom-card">
                  <div className="card-body">
                    <div className="form-group">
                      {compliancesList
                        ?.filter((compliance) => compliance?.is_recurring === '0')
                        ?.map((compliance) => {
                          const currentForm = getFormData(compliance.id);
                          const currentErrors = getErrors(compliance.id);
                          return (
                            <div className="row align-items-center mt-4 mb-4" key={compliance.id}>
                              <div className="col-md-12">
                                <h3 style={{ fontWeight: '300' }}>{compliance?.compliance_name}</h3>
                              </div>
                              <div className="col-md-5">
                                <label>
                                  Date <span className="required text-danger">*</span>
                                </label>
                                <input
                                  name="date"
                                  type="date"
                                  className={`form-control ${currentErrors.date ? 'is-invalid' : ''}`}
                                  value={currentForm.date || ''}
                                  onChange={(e) => handleInputChange(compliance.id, e)}
                                />
                                {currentErrors.date && <span className="text-danger">{currentErrors.date}</span>}
                              </div>
                              <div className="col-md-5">
                                <label>
                                  Document Upload <span className="required text-danger">*</span>
                                </label>
                                <input
                                  key={currentForm.document ? 'file-selected' : 'file-empty'}
                                  name="document"
                                  type="file"
                                  className={`form-control ${currentErrors.document ? 'is-invalid' : ''}`}
                                  onChange={(e) => handleInputChange(compliance.id, e)}
                                />
                                {currentErrors.document && <span className="text-danger">{currentErrors.document}</span>}
                              </div>
                              <div className="col-md-2" style={{ paddingTop: '30px' }}>
                                <button
                                  type="button"
                                  className="btn btn-default"
                                  onClick={(e) => handleSubmit(e, compliance.id, compliance.compliance_name, compliance.is_recurring)}
                                >
                                  Submit
                                </button>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Recurring Compliance Section */}
        <div className="container mb-4">
          <div className="page-header">
            <div>
              <h2 className="main-content-title tx-24 mg-b-5">Recurring Compliance</h2>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <form onSubmit={(e) => e.preventDefault()}>
                {compliancesList
                  ?.filter((compliance) => compliance?.is_recurring === '1')
                  ?.map((compliance, index) => {
                    const isOpen = openFormId === compliance.id;
                    const currentForm = getFormData(compliance.id);
                    const currentErrors = getErrors(compliance.id);
                    return (
                      <div className="card custom-card mb-4" key={compliance.id || index}>
                        <div className="card-body">
                          <div className="form-group">
                            <div className="row align-items-center mt-4 mb-4">
                              <div className="col-md-12">
                                <h3 style={{ fontWeight: '300' }}>{compliance?.compliance_name}</h3>
                              </div>
                              <div className="col-md-12 mt-2">
                                <button type="button" className="btn btn-default" onClick={() => handleToggleForm(compliance.id)}>
                                  Mark As Complete
                                </button>
                              </div>

                              {isOpen && (
                                <>
                                  <div className="col-md-4 mt-3">
                                    <label>
                                      Date of Compliance <span className="required text-danger">*</span>
                                    </label>
                                    <input
                                      name="date"
                                      type="date"
                                      className={`form-control ${currentErrors.date ? 'is-invalid' : ''}`}
                                      value={currentForm.date || ''}
                                      onChange={(e) => handleInputChange(compliance.id, e)}
                                    />
                                    {currentErrors.date && <span className="text-danger">{currentErrors.date}</span>}
                                  </div>
                                  <div className="col-md-4 mt-3">
                                    <label>
                                      Document Upload <span className="required text-danger">*</span>
                                    </label>
                                    <input
                                      key={currentForm.document ? 'file-selected' : 'file-empty'}
                                      name="document"
                                      type="file"
                                      className={`form-control ${currentErrors.document ? 'is-invalid' : ''}`}
                                      onChange={(e) => handleInputChange(compliance.id, e)}
                                    />
                                    {currentErrors.document && <span className="text-danger">{currentErrors.document}</span>}
                                  </div>
                                  <div className="col-md-4 mt-3">
                                    <label>
                                      Next Due Date <span className="required text-danger">*</span>
                                    </label>
                                    <input
                                      name="next_due_date"
                                      type="date"
                                      className={`form-control ${currentErrors.next_due_date ? 'is-invalid' : ''}`}
                                      value={currentForm.next_due_date || ''}
                                      onChange={(e) => handleInputChange(compliance.id, e)}
                                    />
                                    {currentErrors.next_due_date && <span className="text-danger">{currentErrors.next_due_date}</span>}
                                  </div>
                                  <div className="col-md-12 mt-3">
                                    <label>
                                      Remark <span className="required text-danger">*</span>
                                    </label>
                                    <textarea
                                      name="remarks"
                                      className={`form-control ${currentErrors.remarks ? 'is-invalid' : ''}`}
                                      rows="3"
                                      value={currentForm.remarks || ''}
                                      onChange={(e) => handleInputChange(compliance.id, e)}
                                    ></textarea>
                                    {currentErrors.remarks && <span className="text-danger">{currentErrors.remarks}</span>}
                                  </div>
                                  <div className="col-md-2 mt-3">
                                    <button
                                      type="button"
                                      className="btn btn-default"
                                      onClick={(e) => handleSubmit(e, compliance.id, compliance.compliance_name, compliance.is_recurring)}
                                    >
                                      Save
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>

                            {/* Table to display compliance details */}
                            <div className="table-responsive mt-4">
                              <table className="table table-bordered">
                                <thead className="thead-light">
                                  <tr>
                                    <th>Date</th>
                                    <th>Document</th>
                                    <th>Remark</th>
                                    <th>Next Date</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {listCompliance
                                    ?.filter((complianceData) => complianceData.complianceId === compliance.id)
                                    ?.map((complianceData) =>
                                      complianceData?.data?.map((item, idx) => (
                                        <tr key={item.id || idx}>
                                          <td>{item.compliance_date || 'Not Provided'}</td>
                                          <td>
                                            {item.compliance_document ? (
                                              <a
                                                href={getDocumentUrl(item.compliance_document)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                              >
                                                View Document
                                              </a>
                                            ) : (
                                              'Not Uploaded'
                                            )}
                                          </td>
                                          <td>{item.remarks || 'No Remarks'}</td>
                                          <td>{item.next_due_date || 'Not Provided'}</td>
                                        </tr>
                                      ))
                                    )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </form>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Compliance;
